import { useCallback, useContext } from 'react';
import { AlertTriangle, RefreshCw, X } from 'react-feather';
import styled from '@emotion/styled';
import { ChatStateDispatchContext } from '@trinity/chat-components/components/Chat.State/context';
import { FileIcon } from '@trinity/chat-components/components/icons/File/index.js';
import { ProgressRing } from '@trinity/chat-components/components/presentation/ProgressRing';
import { shouldForwardProp } from '@trinity/chat-components/utils/emotion';
import type { Theme } from '../../types/theme';
import type { PendingAttachmentsState } from '../Chat.State/interfaces.pending-attachments';
import { ChatSessionPendingAttachmentsContext } from './context';

type Props = {
  onRetry: (attachment: PendingAttachmentsState.PendingAttachment) => void;
};

function formatBytes(bytes: number): string {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function extensionFromName(name: string): string {
  return name.split('.').pop() ?? '';
}

// Renders a row of file preview cards above the text input, one per staged or in-progress attachment.
export function StagedFiles({ onRetry }: Props) {
  const pendingAttachments = useContext(ChatSessionPendingAttachmentsContext);
  const dispatch = useContext(ChatStateDispatchContext);

  const handleRemove = useCallback(
    (clientId: string) => {
      dispatch({ type: 'pending-attachment/file-removed', payload: { clientId } });
    },
    [dispatch]
  );

  if (pendingAttachments.length === 0) return null;

  return (
    <Root>
      {pendingAttachments.map((attachment) => {
        const extension = extensionFromName(attachment.name);
        const isError = attachment.status === 'error';
        const isUploading = attachment.status === 'uploading';

        return (
          <Card key={attachment.clientId} status={attachment.status}>
            <CardIcon isError={isError}>
              {isError ? <AlertTriangle size={18} /> : <FileIcon extension={extension} size={28} />}
            </CardIcon>

            <CardBody>
              <CardName title={attachment.name}>{attachment.name}</CardName>
              {isError ? (
                <CardError>Upload failed</CardError>
              ) : (
                <CardSize>{formatBytes(attachment.sizeBytes)}</CardSize>
              )}
            </CardBody>

            {isUploading && <ProgressRing progress={attachment.progress} size={18} />}

            {isError && (
              <IconButton aria-label="Retry upload" onClick={() => onRetry(attachment)}>
                <RefreshCw size={14} />
              </IconButton>
            )}

            {!isUploading && (
              <IconButton
                aria-label={`Remove ${attachment.name}`}
                onClick={() => handleRemove(attachment.clientId)}
              >
                <X size={14} />
              </IconButton>
            )}
          </Card>
        );
      })}
    </Root>
  );
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const Root = styled('div')({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 8,
  padding: '6px 0 4px',
});

type CardStatus = PendingAttachmentsState.PendingAttachment['status'];

function cardBorderColor(theme: Theme.Theme, status: CardStatus): string {
  if (status === 'error') return theme.palette.red.main;
  return theme.palette.gray.light1;
}

function cardBackground(status: CardStatus): string {
  if (status === 'error') return 'rgba(225, 46, 64, 0.04)';
  return '#ffffff';
}

const Card = styled('div', { shouldForwardProp: shouldForwardProp('status') })<{ status: CardStatus }>(
  ({ theme, status }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 10px',
    borderRadius: 10,
    border: `1px solid ${cardBorderColor(theme, status)}`,
    backgroundColor: cardBackground(status),
    maxWidth: 220,
    minWidth: 140,
  })
);

const CardIcon = styled('div', { shouldForwardProp: shouldForwardProp('isError') })<{ isError: boolean }>(
  ({ theme, isError }) => ({
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: isError ? theme.palette.red.main : 'inherit',
  })
);

const CardBody = styled('div')({
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
});

const CardName = styled('span')(({ theme }) => ({
  fontSize: 13,
  fontFamily: theme.fonts.semiBold,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  color: theme.palette.gray.dark1,
}));

const CardSize = styled('span')(({ theme }) => ({
  fontSize: 11,
  color: theme.palette.gray.main,
}));

const CardError = styled('span')(({ theme }) => ({
  fontSize: 11,
  color: theme.palette.red.main,
}));

const IconButton = styled('button')(({ theme }) => ({
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'none',
  border: 'none',
  padding: 2,
  cursor: 'pointer',
  color: theme.palette.gray.main,
  borderRadius: 4,
  '&:hover': {
    color: theme.palette.gray.dark1,
    backgroundColor: theme.palette.gray.light2,
  },
}));
